// Funnel v2 client (OWNER DIRECTIVE 2026-06-12) — talks to the roleforge-app
// Pages Functions, which proxy to the VibeControl server with the service
// token.
//
//   startPaymentIntent  → /api/checkout          (LTO + bumps; returns the
//                                                 PaymentIntent client secret for
//                                                 inline Elements card collection)
//   acceptTrialUpgrade  → /api/trial-consent     (free OTO; one click — consent is
//                                                 recorded against the card saved
//                                                 at checkout, no charge, no UI)
//   requestLoginLink    → /api/auth-request-link (issue #983 items 1+5; branded
//                                                 magic-link email with rate-limit
//                                                 feedback, replacing the /login
//                                                 page's direct signInWithOtp call)
//   claimSession        → /api/claim-session     (issue #978; mints a same-device
//                                                 sign-in token for a just-succeeded
//                                                 purchase, so the thank-you page's
//                                                 CTA can skip the login wall)

/**
 * sessionStorage key holding the PaymentIntent client_secret of the purchase
 * made in THIS browser (issue #1004). The claim endpoint requires it, so a
 * PaymentIntent id alone — which appears in receipts, logs and dashboards — is
 * no longer sufficient to mint a session as the buyer.
 */
export const CLAIM_SECRET_STORAGE_KEY = 'rf_claim_secret';

function readClaimSecret(): string {
  try {
    return sessionStorage.getItem(CLAIM_SECRET_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export class FunnelApiError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = 'FunnelApiError';
  }
}

/**
 * The server can decline to create a PaymentIntent for two reasons, both of
 * which protect the buyer from a charge they should not be paying:
 *
 * - `subscriber` — D2 guardrail (docs/requirements/roleforge-continuity.md:122):
 *   active subscribers never pay the $47 again; blueprints are included on
 *   their plan.
 * - `blueprint_in_progress` — issue #1196: this buyer already paid and their
 *   first Blueprint is still in flight. They are almost always here because
 *   they lost the access email or the payment UI reported an error after the
 *   charge actually succeeded. What they need is a sign-in link, not a second
 *   $47 charge.
 */
export type StartPaymentIntentResult =
  | {
      status: 'ready';
      clientSecret: string;
      paymentIntentId: string;
      /**
       * Issue #1321. The exact amount, in cents, the PaymentIntent will charge.
       * The server sums `roleforge_funnel_skus.amount_cents` — the only source
       * of truth for price — and has returned this field since the inline
       * Elements checkout shipped (2026-06-11). It used to be dropped on the
       * floor here while `CheckoutForm` rendered a hardcoded $47, so a price
       * change in the SKU table took effect on the charge while every number
       * the buyer saw kept the old figure.
       *
       * Parsed strictly and FAIL-CLOSED (below): a checkout that cannot state
       * what it is about to charge must not collect a card at all.
       */
      amountCents: number;
    }
  | { status: 'subscriber'; appUrl: string }
  | { status: 'blueprint_in_progress'; appUrl: string };

/**
 * Create the LTO PaymentIntent ($47 Blueprint + optional $27 Quick-Start Kit).
 * Returns the client secret used to mount inline Stripe Elements on /checkout —
 * the card is collected and confirmed in-page, never on a Stripe-hosted page.
 *
 * If the buyer's email belongs to an active subscriber, no PaymentIntent is
 * created — see StartPaymentIntentResult above.
 */
export async function startPaymentIntent(input: {
  email: string;
  bumps: string[];
}): Promise<StartPaymentIntentResult> {
  let res: Response;
  try {
    res = await fetch('/api/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: input.email, bumps: input.bumps }),
    });
  } catch {
    throw new FunnelApiError('checkout_failed');
  }
  if (!res.ok) throw new FunnelApiError('checkout_failed');
  const data = (await res.json().catch(() => null)) as
    | {
        subscriber?: unknown;
        blueprint_in_progress?: unknown;
        app_url?: unknown;
        clientSecret?: unknown;
        paymentIntentId?: unknown;
        amountCents?: unknown;
      }
    | null;
  if (!data) throw new FunnelApiError('checkout_failed');

  if (data.subscriber === true) {
    if (typeof data.app_url !== 'string' || !data.app_url) {
      throw new FunnelApiError('checkout_failed');
    }
    return { status: 'subscriber', appUrl: data.app_url };
  }

  // Issue #1196. Fail closed on a malformed payload the same way the
  // subscriber branch does — never silently fall through to a card form for a
  // buyer the server just told us has already paid.
  if (data.blueprint_in_progress === true) {
    if (typeof data.app_url !== 'string' || !data.app_url) {
      throw new FunnelApiError('checkout_failed');
    }
    return { status: 'blueprint_in_progress', appUrl: data.app_url };
  }

  if (
    typeof data.clientSecret !== 'string' ||
    !data.clientSecret ||
    typeof data.paymentIntentId !== 'string' ||
    !data.paymentIntentId ||
    // Issue #1321: no server amount, no card form. Falling back to a
    // client-side guess is exactly the defect — it lets the buyer authorize a
    // number the server did not decide.
    typeof data.amountCents !== 'number' ||
    !Number.isInteger(data.amountCents) ||
    data.amountCents <= 0
  ) {
    throw new FunnelApiError('checkout_failed');
  }
  return {
    status: 'ready',
    clientSecret: data.clientSecret,
    paymentIntentId: data.paymentIntentId,
    amountCents: data.amountCents,
  };
}

/**
 * Accept the free OTO in ONE CLICK. The server verifies the PaymentIntent
 * succeeded and records consent against the card saved at checkout. There is no
 * second card entry, no setup session, no Stripe UI — it simply resolves on
 * success.
 *
 * Issue #1197: the PaymentIntent id is NOT a credential — it travels in the
 * `/oto?payment_intent_id=…` URL, browser history, Referer headers and Stripe
 * receipts, and this endpoint is reached through an unauthenticated public
 * Pages Function. The `client_secret` Stripe issued to the browser that
 * actually paid is what authorizes arming a trial, so it is sent alongside.
 * The UX is unchanged: it is already in sessionStorage from checkout.
 *
 * Throws `FunnelApiError('trial_consent_not_authorized')` when this browser
 * cannot prove it is the one that paid (or the purchase is outside the proof
 * window), so the UI can say something true instead of "try again".
 */
export async function acceptTrialUpgrade(paymentIntentId: string): Promise<void> {
  // No proof in THIS browser at all — /oto was opened in a different tab or on
  // a different device, or sessionStorage was unavailable when the payment
  // succeeded. Sending the request anyway gets a 400 for a missing field, and
  // 400 falls into the generic branch below, which tells the buyer to "give it
  // a moment and try again" — advice that can never work and loops them
  // forever. Fail here with the honest code so OtoButtons explains the real
  // rule, exactly as it does for a server-side 403.
  const claimSecret = readClaimSecret();
  if (!claimSecret) throw new FunnelApiError('trial_consent_not_authorized');

  let res: Response;
  try {
    res = await fetch('/api/trial-consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
        payment_intent_client_secret: claimSecret,
      }),
    });
  } catch {
    throw new FunnelApiError('trial_consent_failed');
  }
  if (res.status === 403) throw new FunnelApiError('trial_consent_not_authorized');
  if (!res.ok) throw new FunnelApiError('trial_consent_failed');
  const data = (await res.json().catch(() => null)) as { consented?: unknown } | null;
  if (data?.consented === true) return;
  throw new FunnelApiError('trial_consent_failed');
}

/**
 * Result of a login magic-link request (issue #983 items 1 + 5). 'rate_limited'
 * carries how long until the caller can retry, so the UI can show "link
 * already sent — retry in Ns" instead of Supabase's previous silent no-op.
 */
export type RequestLoginLinkResult =
  | { status: 'sent' }
  | { status: 'rate_limited'; retryAfterSeconds: number }
  | { status: 'error' };

/**
 * Request a branded RoleForge sign-in email (replaces the previous direct
 * `supabase.auth.signInWithOtp()` call, which sent Supabase's unbranded
 * stock template). `redirect` is forwarded to the eventual
 * /auth/callback?redirect=... the same way the old client-side call did.
 */
export async function requestLoginLink(input: {
  email: string;
  redirect?: string | null;
}): Promise<RequestLoginLinkResult> {
  let res: Response;
  try {
    res = await fetch('/api/auth-request-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: input.email, redirect: input.redirect ?? undefined }),
    });
  } catch {
    return { status: 'error' };
  }

  if (res.status === 429) {
    const data = (await res.json().catch(() => null)) as { retry_after_seconds?: unknown } | null;
    const retryAfterSeconds = typeof data?.retry_after_seconds === 'number' ? data.retry_after_seconds : 60;
    return { status: 'rate_limited', retryAfterSeconds };
  }
  if (!res.ok) return { status: 'error' };

  const data = (await res.json().catch(() => null)) as { sent?: unknown } | null;
  if (data?.sent === true) return { status: 'sent' };
  return { status: 'error' };
}

/**
 * Result of a claim-session attempt (issue #978). 'pending' means the
 * purchase webhook that provisions the account hasn't landed yet — callers
 * should retry briefly before falling back to the email/login path. 'error'
 * covers anything else (bad/expired payment_intent_id, network failure).
 */
export type ClaimSessionResult =
  | { status: 'ready'; tokenHash: string; email: string }
  | { status: 'pending'; email: string | null }
  | { status: 'error' };

/**
 * Redeem a just-succeeded PaymentIntent for a same-device sign-in token. On
 * 'ready', navigate to `/auth/callback?token_hash=<tokenHash>&type=magiclink`
 * (AuthCallbackPage already knows how to verify a token_hash and establish a
 * session) — no email round-trip required for the buyer's own device.
 */
export async function claimSession(paymentIntentId: string): Promise<ClaimSessionResult> {
  let res: Response;
  try {
    res = await fetch('/api/claim-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
        // Issue #1004: proof that this is the browser that paid.
        payment_intent_client_secret: readClaimSecret(),
      }),
    });
  } catch {
    return { status: 'error' };
  }

  const data = (await res.json().catch(() => null)) as
    | { status?: unknown; token_hash?: unknown; email?: unknown }
    | null;

  if (res.status === 202) {
    return { status: 'pending', email: typeof data?.email === 'string' ? data.email : null };
  }
  if (!res.ok) return { status: 'error' };

  if (data?.status === 'ready' && typeof data.token_hash === 'string' && typeof data.email === 'string') {
    return { status: 'ready', tokenHash: data.token_hash, email: data.email };
  }
  if (data?.status === 'pending') {
    return { status: 'pending', email: typeof data?.email === 'string' ? data.email : null };
  }
  return { status: 'error' };
}
